import React, { useState } from 'react';
import { Eye, Save, BarChart3, UserPlus, Loader2, CheckCircle2, AlertCircle, Copy, Check, Trash2, Mail, Phone, X } from 'lucide-react';
import AnalyticsModal from './AnalyticsModal';
import { doc, deleteDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../firebase"; // adjust if needed
import { useNavigate } from 'react-router-dom';

const PitchBuilderToolbar = ({
  handle,
  onHandleChange,
  handleStatus,
  onSaveHandle,
  isHandleSaving,
  handleDisabled,
  saveSuccess,
  companyId,
  isSaving,
  isAutoSaving,
  onSave,
  activeSection,
  currentUser
}) => {
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [inviteMethod, setInviteMethod] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', firstName: '', lastName: '', phone: '' });
  const navigate = useNavigate();

  // Fallback for handle
  const safeHandle = handle || '';

  // Copy URL
  const handleCopyUrl = async () => {
    const pitchUrl = safeHandle ? `decklink.co/p/${safeHandle}` : 'decklink.co/demo-preview';
    try {
      await navigator.clipboard.writeText(pitchUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Invite modal logic
  const resetInviteModal = () => {
    setShowInviteModal(false);
    setInviteMethod(null);
    setInviteData({ email: '', firstName: '', lastName: '', phone: '' });
  };

  const handleInviteByEmail = () => {
    // Simulate sending email
    alert('Email invite sent successfully!');
    resetInviteModal();
  };

  const handleInviteByPhone = () => {
    const pitchUrl = safeHandle ? `decklink.co/p/${safeHandle}` : 'decklink.co/demo-preview';
    const message = `Check out my pitch deck: ${pitchUrl}`;
    const smsUrl = `sms:?body=${encodeURIComponent(message)}`;
    window.open(smsUrl, '_blank');
    resetInviteModal();
  };

  // Delete logic
  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      // 1. Delete company document
      await deleteDoc(doc(db, "companies", companyId));

      // 2. Delete handle(s) where companyId matches
      const handlesRef = collection(db, "handles");
      const handlesQuery = query(handlesRef, where("companyId", "==", companyId));
      const handlesSnapshot = await getDocs(handlesQuery);
      for (const handleDoc of handlesSnapshot.docs) {
        await deleteDoc(handleDoc.ref);
      }

      // 3. Delete analytics where companyId matches
      const analyticsRef = collection(db, "analytics");
      const analyticsQuery = query(analyticsRef, where("companyId", "==", companyId));
      const analyticsSnapshot = await getDocs(analyticsQuery);
      for (const analyticsDoc of analyticsSnapshot.docs) {
        await deleteDoc(analyticsDoc.ref);
      }

      setIsDeleting(false);
      setShowDeleteModal(false);
      navigate('/dashboard');
    } catch (error) {
      setIsDeleting(false);
      setShowDeleteModal(false);
      console.error("Error deleting company and related data:", error);
    }
  };

  // Preview logic
  const handlePreview = () => {
    if (safeHandle && safeHandle.length >= 4 && handleStatus === 'saved') {
      window.open(`/p/${safeHandle}`, '_blank', 'noopener,noreferrer');
    } else if (!safeHandle || safeHandle.length < 4) {
      alert('Please enter a handle with at least 4 characters to preview your pitch.');
    } else if (handleStatus !== 'saved') {
      alert('Please save your handle first before previewing.');
    }
  };

  return (
    <div className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4">
      {/* Handle Section */}
      <div className="flex flex-col gap-3 sm:gap-2">
        <div className="flex flex-col xs:flex-row xs:items-center gap-2">
          <div className="flex items-center gap-1 min-w-0">
            <span className="text-gray-500 font-medium text-sm whitespace-nowrap">decklink.co/p/</span>
            <input
              type="text"
              value={safeHandle}
              onChange={onHandleChange}
              disabled={handleDisabled}
              className="border-b border-gray-300 focus:border-blue-500 outline-none px-2 py-1 text-sm sm:text-base font-mono bg-white min-w-0 flex-1 xs:w-32 sm:w-40"
              placeholder="your-handle"
              maxLength={32}
              autoComplete="off"
            />
            <button
              onClick={handleCopyUrl}
              className="p-1.5 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg transition-colors"
              title="Copy URL to clipboard"
            >
              {copySuccess ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          </div>
          <div className="flex items-center justify-between xs:justify-start gap-2">
            {/* Handle Status Indicators */}
            <div className="flex items-center gap-2">
              {safeHandle.length > 0 && safeHandle.length < 4 && (
                <span className="text-xs text-gray-400 whitespace-nowrap">Min 4 chars</span>
              )}
              {safeHandle.length >= 4 && handleStatus === 'checking' && (
                <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
              )}
              {safeHandle.length >= 4 && handleStatus === 'available' && (
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              )}
              {safeHandle.length >= 4 && handleStatus === 'taken' && (
                <AlertCircle className="h-4 w-4 text-red-500" />
              )}
            </div>
            <button
              onClick={onSaveHandle}
              disabled={isHandleSaving || handleStatus !== 'available' || handleDisabled}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap"
            >
              {isHandleSaving ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <CheckCircle2 className="h-3 w-3" />
              )}
              <span className="hidden xs:inline">Save URL</span>
              <span className="xs:hidden">Save</span>
            </button>
          </div>
        </div>
        {/* Handle Status Messages */}
        {handleStatus === 'taken' && (
          <div className="text-xs text-red-600 mb-2">This handle is already taken.</div>
        )}
        {handleStatus === 'saved' && (
          <div className="flex items-center gap-1 text-xs text-green-600">
            <CheckCircle2 className="h-3 w-3" />
            <span>URL saved successfully</span>
          </div>
        )}
        {handleStatus === 'error' && (
          <div className="text-xs text-red-600">Failed to save URL. Please try again.</div>
        )}
      </div>
      {/* Action Buttons */}
      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end sm:items-center sm:gap-3">
        <button
          onClick={() => setShowAnalytics(true)}
          className="flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all min-h-[40px]"
        >
          <BarChart3 className="h-4 w-4 flex-shrink-0" />
          <span className="truncate">Analytics</span>
        </button>
        <button
          onClick={() => setShowInviteModal(true)}
          className="flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all min-h-[40px]"
        >
          <UserPlus className="h-4 w-4 flex-shrink-0" />
          <span className="truncate">Invite</span>
        </button>
        <button
          onClick={handlePreview}
          disabled={!safeHandle || safeHandle.length < 4 || handleStatus !== 'saved'}
          className="flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed min-h-[40px]"
        >
          <Eye className="h-4 w-4 flex-shrink-0" />
          <span className="truncate">Preview</span>
        </button>
        <button
          onClick={onSave}
          disabled={isSaving}
          className="flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed min-h-[40px]"
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
          ) : (
            <Save className="h-4 w-4 flex-shrink-0" />
          )}
          <span className="truncate">{isSaving ? 'Saving...' : 'Save'}</span>
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDeleteModal(true)}
            disabled={companyId === 'demo'}
            className="flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed min-h-[40px]"
          >
            <Trash2 className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">Delete</span>
          </button>
          {/* Save Success Message */}
          {saveSuccess && (
            <div className="flex items-center gap-1.5 px-2 py-1.5 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
              <span className="text-green-700 font-medium text-xs whitespace-nowrap">
                {isAutoSaving ? 'Auto-saved' : 'Saved'}
              </span>
            </div>
          )}
        </div>
      </div>
      {/* Modals */}
      <AnalyticsModal show={showAnalytics} onClose={() => setShowAnalytics(false)} companyId={companyId} />
      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
              <h3 className="text-lg font-semibold text-gray-900">Invite to View Pitch</h3>
              <button
                onClick={resetInviteModal}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4">
              {!inviteMethod ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 mb-4">Choose how you'd like to invite someone:</p>
                  <button
                    onClick={() => {
                      const pitchUrl = safeHandle ? `decklink.co/p/${safeHandle}` : 'decklink.co/demo-preview';
                      const subject = encodeURIComponent('Check out my pitch deck');
                      const body = encodeURIComponent(`I'd like to share my pitch deck with you. You can view it here: ${pitchUrl}`);
                      const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
                      const link = document.createElement('a');
                      link.href = mailtoLink;
                      link.target = '_blank';
                      link.rel = 'noopener noreferrer';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    className="w-full flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  >
                    <Mail className="h-5 w-5 text-blue-500 flex-shrink-0" />
                    <div className="text-left min-w-0">
                      <div className="font-medium text-gray-900">Email Invitation</div>
                      <div className="text-sm text-gray-500">Send a personalized email invite</div>
                    </div>
                  </button>
                  <button
                    onClick={() => setInviteMethod('phone')}
                    className="w-full flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  >
                    <Phone className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <div className="text-left min-w-0">
                      <div className="font-medium text-gray-900">Text Message</div>
                      <div className="text-sm text-gray-500">Share via SMS (mobile only)</div>
                    </div>
                  </button>
                </div>
              ) : inviteMethod === 'email' ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Mail className="h-5 w-5 text-blue-500 flex-shrink-0" />
                    <h4 className="font-medium text-gray-900">Email Invitation</h4>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                    <input
                      type="email"
                      value={inviteData.email}
                      onChange={e => setInviteData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter email address"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                      <input
                        type="text"
                        value={inviteData.firstName}
                        onChange={e => setInviteData(prev => ({ ...prev, firstName: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="First name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                      <input
                        type="text"
                        value={inviteData.lastName}
                        onChange={e => setInviteData(prev => ({ ...prev, lastName: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Last name"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col xs:flex-row gap-2 pt-4">
                    <button
                      onClick={() => setInviteMethod(null)}
                      className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleInviteByEmail}
                      disabled={!inviteData.email}
                      className="flex-1 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Send Invite
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Phone className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <h4 className="font-medium text-gray-900">Text Message</h4>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-600 mb-2">Message to send:</p>
                    <p className="text-sm font-mono bg-white p-2 rounded border break-all">
                      Check out my pitch deck: {safeHandle ? `decklink.co/p/${safeHandle}` : 'decklink.co/demo-preview'}
                    </p>
                  </div>
                  <div className="flex flex-col xs:flex-row gap-2 pt-4">
                    <button
                      onClick={() => setInviteMethod(null)}
                      className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleInviteByPhone}
                      className="flex-1 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all"
                    >
                      Open Messages
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-full">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Company</h3>
              </div>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this company? This action cannot be undone and will permanently remove all associated data.
              </p>
              <div className="flex flex-col xs:flex-row gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Company'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PitchBuilderToolbar; 